/**
 * Pure helpers behind the AI workspace action cards (action_confirmation,
 * ai_selection, action_run): status wording, the expiry clock, run progress,
 * the problems CSV and the "Open in <screen>" route. No Vue, no Apollo, so
 * every rule here is unit-tested on its own.
 */
import type { RouteLocationRaw, Router } from 'vue-router';
import {
  AiActionRunItemStatus,
  AiActionRunStatus,
  AiPendingActionStatus,
  AiPreviewChangeKind,
  type AiActionRun,
  type AiActionRunItem,
} from '@/types/composition-functions';

/** PrimeVue Tag / Message severities the cards use. */
export type AiCardSeverity = 'success' | 'info' | 'warn' | 'danger' | 'secondary';

const PENDING_STATUS_LABELS: Record<AiPendingActionStatus, string> = {
  [AiPendingActionStatus.Pending]: 'Awaiting your confirmation',
  [AiPendingActionStatus.Executing]: 'Running',
  [AiPendingActionStatus.Done]: 'Done',
  [AiPendingActionStatus.Failed]: 'Failed',
  [AiPendingActionStatus.Cancelled]: 'Cancelled',
  [AiPendingActionStatus.Expired]: 'Expired',
  [AiPendingActionStatus.Stale]: 'Out of date',
};

const PENDING_STATUS_SEVERITIES: Record<AiPendingActionStatus, AiCardSeverity> = {
  [AiPendingActionStatus.Pending]: 'info',
  [AiPendingActionStatus.Executing]: 'info',
  [AiPendingActionStatus.Done]: 'success',
  [AiPendingActionStatus.Failed]: 'danger',
  [AiPendingActionStatus.Cancelled]: 'secondary',
  [AiPendingActionStatus.Expired]: 'secondary',
  [AiPendingActionStatus.Stale]: 'warn',
};

export function pendingStatusLabel(status: AiPendingActionStatus): string {
  return PENDING_STATUS_LABELS[status] ?? String(status);
}

export function pendingStatusSeverity(status: AiPendingActionStatus): AiCardSeverity {
  return PENDING_STATUS_SEVERITIES[status] ?? 'secondary';
}

/** Milliseconds since the epoch for a server DateTime (ISO 8601 UTC), or null when unparseable. */
export function parseServerTime(value: unknown): number | null {
  if (typeof value !== 'string' || !value) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}

/** Whole seconds left before `expiresAt`, never negative; null when the time is unknown. */
export function secondsUntil(expiresAt: unknown, nowMs: number): number | null {
  const at = parseServerTime(expiresAt);
  if (at === null) return null;
  return Math.max(0, Math.floor((at - nowMs) / 1000));
}

/**
 * The status the card should SHOW. A PENDING action whose clock has run out
 * is shown (and treated) as EXPIRED before the server catches up, so the
 * Confirm button never invites a click the server will refuse.
 */
export function effectivePendingStatus(
  status: AiPendingActionStatus,
  expiresAt: unknown,
  nowMs: number,
): AiPendingActionStatus {
  if (status !== AiPendingActionStatus.Pending) return status;
  const left = secondsUntil(expiresAt, nowMs);
  return left === 0 ? AiPendingActionStatus.Expired : status;
}

/** "14:05" for minutes:seconds, "1:02:09" past an hour. */
export function formatCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

/** Spoken form of the countdown for screen readers ("14 minutes left"). */
export function describeCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  if (s >= 120) return `${Math.floor(s / 60)} minutes left`;
  if (s >= 60) return '1 minute left';
  return s === 1 ? '1 second left' : `${s} seconds left`;
}

// ── Runs ──

export function isRunActive(status: AiActionRunStatus | null | undefined): boolean {
  return status === AiActionRunStatus.Queued || status === AiActionRunStatus.Running;
}

const RUN_STATUS_LABELS: Record<AiActionRunStatus, string> = {
  [AiActionRunStatus.Queued]: 'Queued',
  [AiActionRunStatus.Running]: 'Running',
  [AiActionRunStatus.Done]: 'Finished',
  [AiActionRunStatus.Failed]: 'Failed',
};

const RUN_STATUS_SEVERITIES: Record<AiActionRunStatus, AiCardSeverity> = {
  [AiActionRunStatus.Queued]: 'secondary',
  [AiActionRunStatus.Running]: 'info',
  [AiActionRunStatus.Done]: 'success',
  [AiActionRunStatus.Failed]: 'danger',
};

export function runStatusLabel(status: AiActionRunStatus): string {
  return RUN_STATUS_LABELS[status] ?? String(status);
}

export function runStatusSeverity(status: AiActionRunStatus): AiCardSeverity {
  return RUN_STATUS_SEVERITIES[status] ?? 'secondary';
}

export type AiRunCounters = Pick<AiActionRun, 'total' | 'succeeded' | 'failed' | 'skipped'>;

/** Rows handled so far (done + failed + skipped). */
export function runProcessed(run: AiRunCounters): number {
  return run.succeeded + run.failed + run.skipped;
}

/** 0–100, whole numbers; 100 for an empty run so it never looks stuck. */
export function runProgressPercent(run: AiRunCounters): number {
  if (run.total <= 0) return 100;
  return Math.min(100, Math.round((runProcessed(run) / run.total) * 100));
}

/** True when any row failed or was skipped. */
export function runHasProblems(run: AiRunCounters): boolean {
  return run.failed + run.skipped > 0;
}

export function runItemStatusLabel(status: AiActionRunItemStatus): string {
  if (status === AiActionRunItemStatus.Failed) return 'Failed';
  if (status === AiActionRunItemStatus.Skipped) return 'Skipped';
  return 'Done';
}

export function runItemStatusSeverity(status: AiActionRunItemStatus): AiCardSeverity {
  if (status === AiActionRunItemStatus.Failed) return 'danger';
  if (status === AiActionRunItemStatus.Skipped) return 'warn';
  return 'success';
}

// ── Problems CSV ──

export type AiRunProblemRow = Pick<AiActionRunItem, 'subjectId' | 'label' | 'status' | 'reasonCode' | 'message'>;

export const AI_RUN_PROBLEMS_CSV_HEADERS = ['Record', 'Record ID', 'Outcome', 'Reason code', 'Message'];

/**
 * Quote every cell and double inner quotes. A cell a spreadsheet would read
 * as a formula (=, +, -, @, tab, CR) gets a leading apostrophe, since labels
 * and messages are record text the school typed.
 */
export function csvCell(value: string | null | undefined): string {
  let text = value ?? '';
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
}

export function buildRunProblemsCsv(rows: AiRunProblemRow[]): string {
  const lines = rows.map((row) => [
    csvCell(row.label),
    csvCell(row.subjectId),
    csvCell(runItemStatusLabel(row.status)),
    csvCell(row.reasonCode),
    csvCell(row.message),
  ].join(','));
  return [AI_RUN_PROBLEMS_CSV_HEADERS.join(','), ...lines].join('\n');
}

/** e.g. "approve-submissions-problems-01JB….csv" — safe on every OS. */
export function runProblemsFilename(actionLabel: string | null | undefined, runId: string): string {
  const slug = (actionLabel ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  return `${slug ? `${slug}-` : ''}problems-${runId}.csv`;
}

// ── Structure diffs (form drafts) ──

const CHANGE_KIND_LABELS: Record<AiPreviewChangeKind, string> = {
  [AiPreviewChangeKind.Added]: 'Added',
  [AiPreviewChangeKind.Changed]: 'Changed',
  [AiPreviewChangeKind.Removed]: 'Removed',
  [AiPreviewChangeKind.Moved]: 'Moved',
};

const CHANGE_KIND_SEVERITY: Record<AiPreviewChangeKind, AiCardSeverity> = {
  [AiPreviewChangeKind.Added]: 'success',
  [AiPreviewChangeKind.Changed]: 'info',
  [AiPreviewChangeKind.Removed]: 'danger',
  [AiPreviewChangeKind.Moved]: 'secondary',
};

export function previewChangeLabel(kind: AiPreviewChangeKind): string {
  return CHANGE_KIND_LABELS[kind] ?? kind;
}

export function previewChangeSeverity(kind: AiPreviewChangeKind): AiCardSeverity {
  return CHANGE_KIND_SEVERITY[kind] ?? 'secondary';
}

// ── "Open in <screen>" ──

export interface AiOpenTarget {
  location: RouteLocationRaw;
  /** The screen's name for the button ("Open in Form Submissions"). */
  screenLabel: string;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function toRouteValues(value: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, raw] of Object.entries(value)) {
    if (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') out[key] = String(raw);
  }
  return out;
}

/** "admin-form-submissions" → "Form Submissions" when the route has no meta title. */
function humanizeRouteName(name: string): string {
  return name
    .replace(/^(admin|staff|teacher|student|parent)-/, '')
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Turns a selection's `openRoute` + `openParams` into a router location.
 * `openParams` may carry explicit `params` / `query` objects, flat keys, or a
 * mix (e.g. `{ id, query: { status } }`): flat keys that are path params of
 * the route become params, the rest query; explicit objects win.
 * Returns null — never throws — for a missing or unknown route, or params the
 * route can't take, so the card never shows a button that leads nowhere.
 */
export function resolveAiOpenTarget(
  router: Pick<Router, 'hasRoute' | 'getRoutes' | 'resolve'>,
  openRoute: string | null | undefined,
  openParams: unknown,
): AiOpenTarget | null {
  if (typeof openRoute !== 'string' || !openRoute.trim()) return null;
  try {
    if (!router.hasRoute(openRoute)) return null;
    const record = router.getRoutes().find((r) => r.name === openRoute);
    if (!record) return null;

    const raw = isPlainObject(openParams) ? openParams : {};
    const pathParams = new Set([...record.path.matchAll(/:(\w+)/g)].map((m) => m[1]));
    const flat = toRouteValues(Object.fromEntries(Object.entries(raw).filter(([k]) => k !== 'params' && k !== 'query')));
    const params = {
      ...Object.fromEntries(Object.entries(flat).filter(([k]) => pathParams.has(k))),
      ...toRouteValues(isPlainObject(raw.params) ? raw.params : {}),
    };
    const query = {
      ...Object.fromEntries(Object.entries(flat).filter(([k]) => !pathParams.has(k))),
      ...toRouteValues(isPlainObject(raw.query) ? raw.query : {}),
    };

    const location = { name: openRoute, params, query };
    router.resolve(location);

    const metaTitle = record.meta?.title;
    const screenLabel = typeof metaTitle === 'string' && metaTitle.trim() ? metaTitle.trim() : humanizeRouteName(openRoute);
    return { location, screenLabel };
  } catch {
    return null;
  }
}

/** Share of the largest bucket, 0–100, for the breakdown bars. */
export function bucketPercent(count: number, max: number): number {
  if (max <= 0) return 0;
  return Math.round((Math.max(0, count) / max) * 100);
}
